import { useState, type FC } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getCustomerDiscounts } from 'queries';
import { CustomerDiscountList } from 'components/common/customer-discount-list';
import { Button, Space } from '@g2u/ui-kit';
import { postCustomerDiscounts } from 'queries';
import { Toast, parseErrors } from 'helpers';
import { AddDiscountModal } from 'components/common/add-discount-modal/add-discount-modal.component';
import { AddSystemDiscountModal } from 'components/common/add-system-discount-modal/add-system-discount-modal.component';
import { useDiscountData } from 'helpers';
import { CustomerPrepaidProductsList } from 'components/common/customer-prepaid-products-list';
import { CommentsModal } from 'components/common/comments-modal';
import { AddServiceModal } from 'components/common/add-service-modal';
import { JobLogsModal } from 'components/common/job-logs-modal';
import { BillingHistoryModal } from 'components/common/billing-history-modal';
import { CancelSubscriptionModal } from 'components/common/cancel-subscription-modal';
import { useCustomerProducts } from '../hooks/useCustomerProducts';
import { useAddSubscription } from '../hooks/useAddSubscription';
import { useSubscriptionActions } from '../hooks/useSubscriptionActions';
import { PrepaidItem, PrepaidProductStatus } from 'types';

type Props = Readonly<{
  customerId?: string;
}>;

enum DiscountType {
  SystemDiscount = 'system_discount',
  CustomDiscount = 'custom_discount',
}

export const CustomerDiscounts: FC<Props> = ({ customerId }) => {
  const { data: customerDiscounts, refetch: updateCustomerDiscounts } = useQuery(
    ['discounts', customerId],
    getCustomerDiscounts,
  );
  const { discountReasons, discountTypeTemplates, discountApprovers, systemDiscounts } = useDiscountData();

  const [isAddSystemDiscountModalOpen, setAddSystemDiscountModalOpen] = useState(false);
  const [isAddDiscountModalOpen, setAddDiscountModalOpen] = useState(false);
  const [commentsItem, setCommentsItem] = useState<PrepaidItem | null>(null);
  const [isAddSubscriptionOpen, setAddSubscriptionOpen] = useState(false);
  const [jobLogsSubId, setJobLogsSubId] = useState<number | null>(null);
  const [billingSubId, setBillingSubId] = useState<number | null>(null);
  const [cancelItem, setCancelItem] = useState<PrepaidItem | null>(null);

  const { mutate: addSystemDiscountMutation, isLoading } = useMutation(postCustomerDiscounts, {
    onSuccess: () => {
      updateCustomerDiscounts();
      setAddSystemDiscountModalOpen(false);
      setAddDiscountModalOpen(false);
    },
    onError: (error) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const handleAddDiscount = (values: any, discountType: DiscountType) => {
    let discount;

    switch (discountType) {
      case DiscountType.SystemDiscount:
        discount = {
          system_discount_id: values.type,
          approved_by: values.approver,
        };
        break;

      case DiscountType.CustomDiscount:
        discount = {
          discount_type_template_id: values.type,
          amount: values.amount,
          reason_id: values.reason,
          approved_by: values.approver,
        };
        break;

      default:
        throw new Error('Invalid discount type');
    }

    addSystemDiscountMutation({
      customerId,
      discounts: [discount],
    });
  };

  const { items, paginationMetadata, transferProducts, updateCustomerProductStatus, isLoading: isProductsLoading } =
    useCustomerProducts(customerId);
  const { plans, isSaving, addSubscription } = useAddSubscription({
    customerId,
    enabled: isAddSubscriptionOpen,
    onClose: () => setAddSubscriptionOpen(false),
  });
  const {
    resendTc,
    updateEmailInvoice,
    updateComments,
    cancelSubscription,
    isLoading: isSubscriptionActionLoading,
  } = useSubscriptionActions();
  const handleProductTransfer = (productId: number, customerId: number, name: string) => {
    transferProducts(productId, customerId, name);
  };

  const handleUpdateCustomerProductStatus = (customerProductId: number, status: PrepaidProductStatus) => {
    updateCustomerProductStatus(customerProductId, status);
  };

  return (
    <div>
      <CustomerDiscountList refetch={updateCustomerDiscounts} discounts={customerDiscounts || []} />
      <Space.$ direction={Space.Direction.Horizontal} justify={Space.Justify.End}>
        <Button.$ dataTestId="" onClick={() => setAddSystemDiscountModalOpen(true)}>
          Add System Discount
        </Button.$>
        <AddSystemDiscountModal
          isOpen={isAddSystemDiscountModalOpen}
          onCancel={() => setAddSystemDiscountModalOpen(false)}
          onOk={(values) => handleAddDiscount(values, DiscountType.SystemDiscount)}
          discountTypeTemplates={discountTypeTemplates || []}
          systemDiscounts={systemDiscounts}
          discountApprovers={discountApprovers}
          isLoading={isLoading}
        />
        <Button.$ dataTestId="" onClick={() => setAddDiscountModalOpen(true)}>
          Add Customer Discount
        </Button.$>
        <AddDiscountModal
          isOpen={isAddDiscountModalOpen}
          onCancel={() => setAddDiscountModalOpen(false)}
          onOk={(values) => handleAddDiscount(values, DiscountType.CustomDiscount)}
          discountReasons={discountReasons}
          discountTypeTemplates={discountTypeTemplates || []}
          discountApprovers={discountApprovers}
          isLoading={isLoading}
        />
      </Space.$>
      <br />
      <CustomerPrepaidProductsList
        items={items}
        paginationMetadata={paginationMetadata}
        onTransfer={handleProductTransfer}
        onStatusUpdate={handleUpdateCustomerProductStatus}
        onViewComments={(item) => setCommentsItem(item)}
        onViewJobLogs={(item) => setJobLogsSubId(item.ID)}
        onViewBillingHistory={(item) => setBillingSubId(item.ID)}
        onSendTc={(item) => resendTc(item.ID)}
        onEmailInvoiceSave={(id, emailInvoice) => updateEmailInvoice(id, emailInvoice)}
        onCancelSubscription={(item) => setCancelItem(item)}
        isLoading={isProductsLoading}
      />
      <br />
      <Space.$ direction={Space.Direction.Horizontal} justify={Space.Justify.End}>
        <Button.$ dataTestId="add-service-btn" onClick={() => setAddSubscriptionOpen(true)}>
          Add Subscription
        </Button.$>
      </Space.$>
      <AddServiceModal
        isOpen={isAddSubscriptionOpen}
        onCancel={() => setAddSubscriptionOpen(false)}
        onOk={addSubscription}
        plans={plans}
        isSaving={isSaving}
      />
      <CommentsModal
        isOpen={!!commentsItem}
        id={commentsItem?.ID ?? 0}
        comments={commentsItem?.comments}
        onCancel={() => setCommentsItem(null)}
        onSave={(comments, onDone) => commentsItem && updateComments(commentsItem.ID, comments, onDone)}
        isSaving={isSubscriptionActionLoading}
      />
      <JobLogsModal
        isOpen={!!jobLogsSubId}
        subscriptionId={jobLogsSubId ?? undefined}
        onCancel={() => setJobLogsSubId(null)}
      />
      <BillingHistoryModal
        isOpen={!!billingSubId}
        subscriptionId={billingSubId ?? undefined}
        onCancel={() => setBillingSubId(null)}
      />
      <CancelSubscriptionModal
        isOpen={!!cancelItem}
        isLoading={isSubscriptionActionLoading}
        onCancel={() => setCancelItem(null)}
        onConfirm={(reason, endDate) =>
          cancelItem && cancelSubscription(cancelItem.ID, reason, endDate, () => setCancelItem(null))
        }
      />
    </div>
  );
};
